// Stimulus controller for the AI-Generated Latin Values admin section.
// Used by the Reports tab for status actions, hard delete, and rows-per-page.
// Also mounted on the AI Pronunciations tab to provide shared message popup,
// email copy, and Bootstrap tooltip lifecycle.
import ApplicationController from "./application_controller"

export default class extends ApplicationController {
  static targets = [
    "messageOverlay",  // Full-screen overlay for displaying message popup
    "messageText",     // <p> element that receives the full message text
    "row",             // All table rows (for clearing highlight state)
    "actionDropdown"   // Action dropdowns in Reports tab (for z-index row class)
  ]

  static values = {
    reportsBasePath: String,      // Base path for report action URLs (/ai_latin_value_reports)
    hardDeleteConfirm: String,    // Confirmation message for hard delete
    updateStatusError: String,    // Error message when status update fails
    deleteError: String,          // Error message when delete fails
    copiedMessage: String,        // Success message when email copied
    copyError: String             // Error message when copy fails
  }

  connect() {
    this.refereshIcons();         // Initialize feather icons (inherited from ApplicationController)
    this.bindTooltips();          // Initialize Bootstrap tooltips on email copy icons
    this.bindActionDropdowns();   // Bind dropdown show/hide handlers for Reports tab
  }

  disconnect() {
    this.unbindTooltips();
    this.unbindActionDropdowns();
  }

  // Initialize Bootstrap tooltips for email copy icons
  bindTooltips() {
    this.unbindTooltips();
    this.tooltipElements = this.element.querySelectorAll('[data-bs-toggle="tooltip"]');
    this.tooltipElements.forEach((el) => {
      bootstrap.Tooltip.getOrCreateInstance(el);
    });
  }

  // Clean up tooltip instances to prevent memory leaks
  unbindTooltips() {
    this.tooltipElements?.forEach((el) => bootstrap.Tooltip.getInstance(el)?.dispose());
    this.tooltipElements = [];
  }

  // Attach dropdown event listeners to add z-index class to parent row when dropdown opens.
  // This prevents the dropdown from being clipped by table overflow constraints.
  bindActionDropdowns() {
    this.actionDropdownHandlers = [];

    this.actionDropdownTargets.forEach((dropdown) => {
      const row = dropdown.closest("tr");
      const onShow = () => row?.classList.add("ai-latin-reports-actions-row-open");
      const onHide = () => row?.classList.remove("ai-latin-reports-actions-row-open");

      dropdown.addEventListener("show.bs.dropdown", onShow);
      dropdown.addEventListener("hide.bs.dropdown", onHide);
      this.actionDropdownHandlers.push({ dropdown, onShow, onHide });
    });
  }

  // Remove dropdown event listeners and clear z-index classes
  unbindActionDropdowns() {
    this.actionDropdownHandlers?.forEach(({ dropdown, onShow, onHide }) => {
      dropdown?.removeEventListener("show.bs.dropdown", onShow);
      dropdown?.removeEventListener("hide.bs.dropdown", onHide);
      dropdown?.closest("tr")?.classList.remove("ai-latin-reports-actions-row-open");
    });
  }

  // Updates a report's status via PATCH request.
  // On success, reloads the page to reflect the new tab counts.
  updateStatus(event) {
    const { reportId, status } = event.currentTarget.dataset;
    this.sendRequest(this.reportActionUrl(reportId, "update_status"), "PATCH", { status })
      .then((data) => data.success ? window.location.reload() : this.addFlashMessage(data.error || this.updateStatusErrorValue))
      .catch(() => this.addFlashMessage(this.updateStatusErrorValue));
  }

  // Permanently deletes a report after confirmation.
  // Only available when report status is 'deleted'.
  hardDelete(event) {
    if (!confirm(this.hardDeleteConfirmValue)) return;

    const { reportId } = event.currentTarget.dataset;
    this.sendRequest(this.reportActionUrl(reportId, "hard_delete"), "DELETE")
      .then((data) => data.success ? window.location.reload() : this.addFlashMessage(data.error || this.deleteErrorValue))
      .catch(() => this.addFlashMessage(this.deleteErrorValue));
  }

  // Copies email address to clipboard when user clicks the copy icon.
  // Falls back to legacy execCommand for older browsers.
  copyEmail(event) {
    const email = event.currentTarget.dataset.copyContent;
    if (!email) return;

    // Modern Clipboard API (preferred)
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(email)
        .then(() => this.addFlashMessage(this.copiedMessageValue, "notice"))
        .catch(() => this.addFlashMessage(this.copyErrorValue));
      return;
    }

    // Fallback for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = email;
    textArea.style.cssText = "position:fixed;opacity:0";
    document.body.appendChild(textArea);
    textArea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textArea);
    this.addFlashMessage(ok ? this.copiedMessageValue : this.copyErrorValue, ok ? "notice" : "error");
  }

  // Opens the message popup overlay with the full feedback comment.
  // Highlights the source row and determines the correct highlight class
  // based on context (Reports tab vs AI Pronunciations feedback popup).
  openMessage(event) {
    if (!this.hasMessageOverlayTarget) return;

    const row = document.getElementById(event.currentTarget.dataset.rowId);
    if (!row) return;

    this.messageTextTarget.textContent = row.querySelector("[data-full-message-text]")?.textContent || "";
    this.clearRowHighlight();
    const highlightClass = row.closest("[data-pronunciations-feedback-table]")
      ? "ai-pronunciations-feedback-row-highlighted"
      : "ai-latin-reports-row-highlighted";
    row.classList.add(highlightClass);
    this._activeMessageRowHighlightClass = highlightClass;
    this.messageOverlayTarget.classList.remove("d-none");
  }

  // Closes the message popup when user clicks directly on the overlay background
  closeMessageOnOverlay(event) {
    if (event.target === this.messageOverlayTarget) this.closeMessage();
  }

  // Closes the message popup and clears row highlight
  closeMessage() {
    if (!this.hasMessageOverlayTarget) return;

    this.messageOverlayTarget.classList.add("d-none");
    this.messageTextTarget.textContent = "";
    this.clearRowHighlight();
  }

  // Removes highlight classes from all rows in both tabs
  clearRowHighlight() {
    this.rowTargets.forEach((row) => {
      row.classList.remove("ai-latin-reports-row-highlighted", "ai-pronunciations-feedback-row-highlighted");
    });
    // Also clear any rows in dynamically-loaded feedback popup tables
    if (this._activeMessageRowHighlightClass) {
      this.element.querySelectorAll(`.${this._activeMessageRowHighlightClass}`).forEach((row) => {
        row.classList.remove(this._activeMessageRowHighlightClass);
      });
      this._activeMessageRowHighlightClass = null;
    }
  }

  // Submits the per-page form when user changes the select dropdown
  submitPerPageForm(event) {
    event.target.closest("form").submit();
  }

  // Constructs the URL for report-specific actions (update_status, hard_delete)
  reportActionUrl(reportId, action) {
    return `${this.reportsBasePathValue}/${reportId}/${action}`;
  }

  // Sends a JSON request to the server with CSRF protection
  sendRequest(url, method, body = null) {
    return fetch(url, {
      method,
      body:    body ? JSON.stringify(body) : null,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content"),
      },
    }).then((response) => response.json());
  }
}
